import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Field, Input, Text } from "@fluentui/react-components";
import { Eye24Regular, EyeOff24Regular } from "@fluentui/react-icons";
import { ROUTES } from "#root/routes/constants";
import { showToast } from "#root/components/GlobalToaster";
import MetaData from "../MetaData";
import { useResetPassword } from "#root/queries/auth";
import { withTenantId } from '#root/hooks/useTenantWrappedQuery';
import "./styles.scss";
import { VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import { AxiosError } from "axios";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const tokenParam = searchParams.get("token") || "";

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetPasswordMutation = useResetPassword();

  const tenantIdParam = searchParams.get("tenantId") || "";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newPassword = formData.get("newPassword") as string;
    const repeatPassword = formData.get("repeatPassword") as string;

    if (!newPassword || !repeatPassword) {
      showToast("Please fill in all fields", "error", "top");
      return;
    }
    if (newPassword.length < 8) {
      showToast("Password must be at least 8 characters long", "error", "top");
      return;
    }
    if (newPassword !== repeatPassword) {
      showToast("Passwords do not match", "error", "top");
      return;
    }

    try {
      await withTenantId(async () => {
        await resetPasswordMutation.mutateAsync({ email: emailParam, token: tokenParam, newPassword });
        showToast("Password updated successfully", "success", "top");
        navigate(ROUTES.LOGIN);
      }, tenantIdParam);
    } catch (error) {
      const errorMessage = error instanceof AxiosError 
        ? error.response?.data?.Message.replace(/\s*\([^)]*\)/g, '')
        : undefined;
      
      showToast(
        errorMessage || 'Failed to reset password, please try again later',
        'error',
        'top'
      );
    }
  };

  return (
    <>
      <MetaData title="Reset Password" description="Reset your EMC account password" />
      <div className="reset-password-page">
        <div className="reset-password-page__left" />
        <div className="reset-password-page__right">
          <div className="reset-password-page__form-container">
            <div className="reset-password-page__form-header">
              <img src="/images/emc-logo.svg" alt="EMC Logo" className="reset-password-page__logo" />
            </div>
            <h1 className="reset-password-page__title">Reset Password</h1>
            <form onSubmit={handleSubmit}>
              <Field className="form-field" label="New Password" required hint={VALIDATION_MESSAGES.PASSWORD}>
                <Input
                  pattern={VALIDATION_PATTERNS.PASSWORD}
                  title={VALIDATION_MESSAGES.PASSWORD}
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  required
                  contentAfter={
                    <Text
                      as="span"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="password-toggle"
                    >
                      {showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                    </Text>
                  }
                />
              </Field>

              <Field className="form-field" label="Confirm New Password" required hint={VALIDATION_MESSAGES.PASSWORD}>
                <Input
                  id="repeatPassword"
                  name="repeatPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  required
                  contentAfter={
                    <Text
                      as="span"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="password-toggle"
                    >
                      {showConfirmPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                    </Text>
                  }
                />
              </Field>

              <button type="submit" disabled={resetPasswordMutation.isPending}>
                {resetPasswordMutation.isPending ? "Updating..." : "Reset Password"}
              </button>

              <div className="go-back-text">
                <Link to="/login">Back to Login</Link>
              </div>
            </form>
          </div>

          <div className="reset-password-page__footer">
            <p className="reset-password-page__copyright">
              &copy; {new Date().getFullYear()} EMC
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
